import { mapMutations } from 'vuex';

import metricTranslatorMixin from '@/mixins/translator/metric';

import MetricIcon from '@svgIcons/dashboard/metric.svg';
import CloseIcon from '@svgIcons/close.svg';
import GearIcon from '@svgIcons/gear.svg';

import Switch from '@/components/Common/Switch';
import InputField from '@/components/Common/InputField';

import { SingleMetricOptionMixin } from '../../../mixin';

import {
    CardWrapper,
    GoalCard,
    CardHeader,
    MetricLabel,
    IconWrapper,
    CardContent,
    GoalValueWrapper,
    ItemBox,
    SwitchWrapper,
    SwitchLabel,
} from './styles';

import { isValueExist, mapIndexed, roundToN } from '@/utils/utils';
import { checkIsNumericGoalValue, getGoalRangeActive } from '../../utils';
import { IFRAME_EVENT_MESSAGE } from '@/utils/consts';
import { DEFAULT_GOAL_RANGE_SETTING_4 } from '../../const';
import { makeMetricFormat } from '../../../utils';

export default {
    name: 'TargetItem',
    mixins: [SingleMetricOptionMixin, metricTranslatorMixin],
    props: {
        panelKey: String,
        itemData: {
            type: Object,
            default() {
                return {};
            },
        },
        index: {
            type: Number,
            default: 0,
        },
        updateChildWidgetGoal: {
            type: Function,
            default: R.identity,
        },
        canEditCustomMetric: {
            type: Boolean,
            default: true,
        },
    },
    data() {
        return {
            iframeListeners_: [
                {
                    eventType: IFRAME_EVENT_MESSAGE.UPDATE_METRIC_PANEL,
                    listener: (e) => {
                        const { data } = e;
                        if (!data || data.panelKey !== this.panelKey) return;

                        const { selectedFields } = data;
                        const field = selectedFields[0];
                        if (!field) return;

                        const updated = R.compose(
                            R.assocPath(['goalRange', 'active'], false),
                            R.assocPath(['goalInfo', 'metric'], field),
                            R.assocPath(['goalInfo', 'type'], 1)
                        )(this.itemData);

                        this.updateChildWidgetGoal(updated);
                    },
                },
            ],
        };
    },
    computed: {
        title() {
            return this.$t('FIELD_PANEL.metric.second_panel_title');
        },
        mainGuide() {
            return this.$t('FIELD_PANEL.metric.second_panel_main_guide');
        },
        subGuide() {
            return this.$t('FIELD_PANEL.metric.second_panel_sub_guide');
        },
        currentList() {
            const targetMetric = this.itemData?.goalInfo?.metric;
            return targetMetric ? [{ key: targetMetric }] : [];
        },
    },
    methods: {
        ...mapMutations('newDashboard', ['setGoalRangeSettingData']),
    },
    render() {
        const { itemData: data, index } = this;

        const {
            key,
            goal,
            goalInfo = {
                value: undefined,
                metric: undefined,
                type: 0,
            },
            id,
        } = data;

        const isNumericGoalValue = checkIsNumericGoalValue(data);
        const rangeActive = getGoalRangeActive(data);

        // TODO: delete defensive logic
        if (goalInfo.value === undefined && isValueExist(goal)) {
            goalInfo.value = goal;
        }

        const showValue = goalInfo.type === 0;

        const targetMetricText = this.getMetricName(
            makeMetricFormat(data?.key ?? '')
        );
        const goalMetricText = this.getMetricName(
            makeMetricFormat(goalInfo.metric ?? '')
        );

        return this.wrapTooltip({
            visible: false,
            title: targetMetricText,
            content: (
                <CardWrapper
                    className={'goal-metrics__card_wrapper'}
                    key={`${key}-${index}-${id}`}
                    draggable={false}
                >
                    <GoalCard
                        className={'goal-metrics__card'}
                        data-widget-id={id}
                    >
                        <CardHeader>
                            <MetricLabel>
                                {`${index + 1}. ${targetMetricText}`}
                            </MetricLabel>
                            <div>
                                {!showValue && (
                                    <GoalValueWrapper>
                                        {this.wrapTooltip({
                                            visible: true,
                                            title: goalMetricText,
                                            content: (
                                                <ItemBox data-metric-name={key}>
                                                    <div class="item-text">
                                                        {goalMetricText}
                                                    </div>
                                                    <CloseIcon
                                                        class="close"
                                                        v-view-box
                                                        onClick={() => {
                                                            const nextData =
                                                                R.compose(
                                                                    R.assocPath(
                                                                        [
                                                                            'goalInfo',
                                                                            'metric',
                                                                        ],
                                                                        undefined
                                                                    ),
                                                                    R.assocPath(
                                                                        [
                                                                            'goalInfo',
                                                                            'type',
                                                                        ],
                                                                        0
                                                                    )
                                                                )(data);

                                                            this.updateChildWidgetGoal(
                                                                nextData
                                                            );
                                                        }}
                                                    />
                                                </ItemBox>
                                            ),
                                        })}
                                    </GoalValueWrapper>
                                )}
                                {showValue && (
                                    <InputField
                                        className={'goal-metrics__input-field'}
                                        value={String(goalInfo.value ?? '')}
                                        inputType={'number'}
                                        placeholder={this.$t(
                                            'WIDGET.widget.goal.target_value_placeholder'
                                        )}
                                        liftPlaceholder={true}
                                        onclick={(e) => e.stopPropagation()}
                                        onkeydown={(e) => {
                                            const MINUS_KEY_CODE = 189;
                                            if (e.keyCode === MINUS_KEY_CODE) {
                                                event.preventDefault();
                                            }
                                        }}
                                        onchange={(e) => {
                                            const value = e.target.value;

                                            if (
                                                value === '0' ||
                                                value < 0 ||
                                                value >= Number.MAX_SAFE_INTEGER
                                            ) {
                                                return;
                                            }

                                            let nextValue = value;
                                            if (nextValue !== '') {
                                                const valueAsNumber =
                                                    parseFloat(nextValue);
                                                const isValueValid =
                                                    !isNaN(valueAsNumber);
                                                if (!isValueValid) return;
                                                nextValue = valueAsNumber;
                                            }

                                            const nextData = R.compose(
                                                R.when(
                                                    R.pathSatisfies(
                                                        isValueExist,
                                                        ['goalRange', 'values']
                                                    ),
                                                    R.evolve({
                                                        goalRange: {
                                                            values: mapIndexed(
                                                                (
                                                                    value,
                                                                    idx,
                                                                    arr
                                                                ) => ({
                                                                    ...value,
                                                                    value: roundToN(
                                                                        (nextValue /
                                                                            arr.length) *
                                                                            (idx +
                                                                                1),
                                                                        2
                                                                    ),
                                                                })
                                                            ),
                                                            active: (active) =>
                                                                nextValue
                                                                    ? active
                                                                    : false,
                                                        },
                                                    })
                                                ),
                                                R.assocPath(
                                                    ['goalInfo', 'type'],
                                                    0
                                                ),
                                                R.assocPath(
                                                    ['goalInfo', 'value'],
                                                    nextValue
                                                )
                                            )(data);

                                            this.updateChildWidgetGoal(
                                                nextData
                                            );
                                        }}
                                    />
                                )}
                            </div>

                            {/* TODO: delete */}
                            {this.wrapTooltip({
                                className: 'black-tooltip',
                                visible: true,
                                title: this.$t(
                                    'WIDGET.widget.goal.metric_goal_tooltip'
                                ),
                                placement: 'topRight',
                                content: (
                                    <IconWrapper
                                        onclick={() => {
                                            if (!this.isSecondPanelOpen) {
                                                this.openReactPanel();
                                            }
                                            this.toggleSecondPanel();
                                        }}
                                    >
                                        <MetricIcon class="metric-icon" />
                                    </IconWrapper>
                                ),
                            })}
                        </CardHeader>
                        <CardContent
                            className={'goal-metrics__card-content'}
                        ></CardContent>
                    </GoalCard>
                    <SwitchWrapper>
                        <Switch
                            disabled={!isNumericGoalValue}
                            type="xSmall"
                            value={rangeActive}
                            onClick={() => {
                                const defaultValues = mapIndexed(
                                    (v, idx, arr) => ({
                                        ...v,
                                        value: roundToN(
                                            (goalInfo.value / arr.length) *
                                                (idx + 1),
                                            2
                                        ),
                                    })
                                )(DEFAULT_GOAL_RANGE_SETTING_4);

                                const values = R.pathOr(defaultValues, [
                                    'goalRange',
                                    'values',
                                ])(data);

                                const nextData = R.compose(
                                    R.assocPath(
                                        ['goalRange', 'values'],
                                        values
                                    ),
                                    R.assocPath(
                                        ['goalRange', 'active'],
                                        !rangeActive
                                    )
                                )(data);

                                if (!rangeActive) {
                                    this.setGoalRangeSettingData(nextData);
                                    this.postIframeMessage({
                                        type: IFRAME_EVENT_MESSAGE.TOGGLE_GOAL_WIDGET_RANGE_SETTING,
                                        origin: 'VUE',
                                        data: { childWidgetData: nextData },
                                    });
                                }

                                this.updateChildWidgetGoal(nextData);
                            }}
                        />
                        <SwitchLabel>
                            {this.$t('WIDGET.widget.goal.range_setting_title')}
                        </SwitchLabel>
                        {isNumericGoalValue && rangeActive && (
                            <GearIcon
                                class="gear-icon"
                                onClick={() => {
                                    this.setGoalRangeSettingData(data);
                                    this.postIframeMessage({
                                        type: IFRAME_EVENT_MESSAGE.TOGGLE_GOAL_WIDGET_RANGE_SETTING,
                                        origin: 'VUE',
                                        data: { childWidgetData: data },
                                    });
                                }}
                            />
                        )}
                    </SwitchWrapper>
                </CardWrapper>
            ),
        });
    },
};
